import { useState } from "react";

import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { AnimatePresence } from "framer-motion";
import { twJoin } from "tailwind-merge";

import { DateTimePicker } from "src/base-components/DateTimePicker";
import { Icon } from "src/base-components/Icon";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { OperatorDropdown } from "src/base-components/OperatorDropdown";
import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import { TimeUnit } from "src/clients/jobs";
import { isUnaryOperator } from "src/decisionTableNode/ConditionEditor/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { TimedeltaPicker } from "src/jobs/common/entitySource/TimedeltaPicker";
import { Operators } from "src/jobs/common/entitySource/types";
import {
  adjustDateForUtc,
  fromUnixSeconds,
  getAvailableOperatorsOptions,
  isUnaryOperators,
  OperatorsWithNot,
  operatorMap,
  toUnixSeconds,
  translateToOperator,
} from "src/jobs/common/entitySource/utils";
import { formatDate } from "src/utils/datetime";
import { pluralize } from "src/utils/stringUtils";

enum Mode {
  ABSOLUTE = "absolute",
  RELATIVE = "relative",
}

type Value = {
  value:
    | { offset: number; unit: TimeUnit }
    | { timestamp_seconds: number }
    | null;
  operator: OperatorsWithNot;
};

type Props = {
  dataLoc: string;
  operatorDataLoc: string;
  error: string | undefined;
  onChange: (value: Value) => void;
  value: Value;
  fieldType:
    | PropertyDefinitionOutputTypeEnum.DATE
    | PropertyDefinitionOutputTypeEnum.DATETIME;
};

const options = getAvailableOperatorsOptions(
  PropertyDefinitionOutputTypeEnum.DATETIME,
);

const NavItem = ({
  label,
  back,
  dataLoc,
  onClick,
}: {
  label: string;
  back?: boolean;
  dataLoc: string;
  onClick: () => void;
}) => {
  return (
    <div
      className={twJoin(
        "flex cursor-pointer items-center gap-x-2 border-gray-200 px-4 py-3 font-inter-normal-13px text-gray-800",
        back
          ? "flex-row-reverse justify-end"
          : "justify-between not-last:border-b",
      )}
      data-loc={dataLoc}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick();
      }}
    >
      <span>{label}</span>
      <Icon
        color="text-gray-600"
        icon={back ? faChevronLeft : faChevronRight}
        size="xs"
      />
    </div>
  );
};

export const DateDatetimeSelector: React.FC<Props> = ({
  dataLoc,
  operatorDataLoc,
  error,
  fieldType,
  onChange,
  value,
}) => {
  const isRelativeValue =
    value.value !== null &&
    typeof value.value === "object" &&
    "offset" in value.value &&
    "unit" in value.value;
  const isAbsoluteValue =
    value.value !== null &&
    typeof value.value === "object" &&
    "timestamp_seconds" in value.value;
  const isDateField = fieldType === PropertyDefinitionOutputTypeEnum.DATE;

  const pickMode = () => {
    if (isAbsoluteValue) return Mode.ABSOLUTE;
    if (isRelativeValue) return Mode.RELATIVE;

    return null;
  };

  const [mode, _setMode] = useState<Mode | null>(pickMode());

  const setMode = (newMode: Mode | null) => {
    _setMode(newMode);

    switch (newMode) {
      case Mode.ABSOLUTE:
        onChange({
          value: isAbsoluteValue
            ? value.value
            : { timestamp_seconds: toUnixSeconds(new Date()) },
          operator: value.operator,
        });
        break;
      case Mode.RELATIVE:
        onChange({
          value: isRelativeValue
            ? value.value
            : {
                offset: -1,
                unit: TimeUnit.DAYS,
              },
          operator: value.operator,
        });
    }
  };

  return (
    <Tooltip disabled={!error} title={error} asChild>
      <div
        className={twJoin(
          "flex justify-between rounded-lg border",
          error ? "border-red-400" : "border-gray-200",
        )}
      >
        <div className="flex items-center border-transparent border-y py-1 pl-2">
          <OperatorDropdown
            dataLoc={operatorDataLoc}
            options={options}
            value={translateToOperator(value.operator)}
            onChange={(operator) => {
              onChange({
                value: isUnaryOperator(operator) ? null : value.value,
                operator: operatorMap[operator] as Operators,
              });
            }}
          />
        </div>
        <Popover className="flex w-full items-center overflow-hidden">
          {({ open }) => (
            <>
              <PopoverButton
                className="flex w-full items-center justify-between overflow-hidden pr-2 pl-2.5 text-left font-inter-medium-12px text-gray-600"
                data-loc={`${dataLoc}-button`}
                disabled={isUnaryOperators(value.operator)}
              >
                <span className="truncate">
                  {(() => {
                    if (
                      isRelativeValue &&
                      value &&
                      value.value &&
                      "unit" in value.value &&
                      "offset" in value.value
                    ) {
                      const unitName = value.value.unit.slice(0, -1);
                      return `${pluralize(Math.abs(value.value.offset), unitName)} ago`;
                    }

                    if (
                      isAbsoluteValue &&
                      value.value &&
                      "timestamp_seconds" in value.value
                    ) {
                      return formatDate(
                        adjustDateForUtc(
                          isDateField,
                          fromUnixSeconds(value.value.timestamp_seconds),
                        ),
                        isDateField ? "d MMM yyyy" : undefined,
                      );
                    }
                  })()}
                </span>
                <Icon color="text-gray-600" icon={faChevronDown} size="xs" />
              </PopoverButton>
              <AnimatePresence onExitComplete={() => setMode(pickMode())}>
                {open && (
                  <PopoverPanel
                    anchor="bottom start"
                    as={MotionDropdownPanel}
                    className="w-70"
                    portal
                    static
                  >
                    {mode !== null && (
                      <>
                        <NavItem
                          dataLoc={`${dataLoc}-back`}
                          label={
                            mode === "absolute"
                              ? `Select specific ${fieldType}`
                              : `Select relative ${fieldType}`
                          }
                          back
                          onClick={() => setMode(null)}
                        />

                        {mode === "relative" && (
                          <TimedeltaPicker
                            isDatetime={
                              fieldType ===
                              PropertyDefinitionOutputTypeEnum.DATETIME
                            }
                            value={
                              isRelativeValue &&
                              value.value &&
                              "offset" in value.value &&
                              "unit" in value.value
                                ? value.value
                                : {
                                    offset: -1,
                                    unit: TimeUnit.DAYS,
                                  }
                            }
                            onChange={(newValue) => {
                              onChange({
                                ...value,
                                value: {
                                  offset: newValue.offset,
                                  unit: newValue.unit ?? TimeUnit.DAYS,
                                },
                              });
                            }}
                          />
                        )}
                        {mode === "absolute" && (
                          <div className="decideScrollbar relative z-10 w-full origin-top overflow-hidden">
                            <DateTimePicker
                              dateOnly={isDateField}
                              value={
                                isAbsoluteValue &&
                                value.value &&
                                "timestamp_seconds" in value.value
                                  ? fromUnixSeconds(
                                      value.value.timestamp_seconds,
                                    )
                                  : new Date()
                              }
                              onChange={(date) => {
                                onChange({
                                  ...value,
                                  value: date
                                    ? { timestamp_seconds: toUnixSeconds(date) }
                                    : null,
                                });
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}
                    {mode === null && (
                      <>
                        <NavItem
                          dataLoc={`${dataLoc}-select-specific`}
                          label={`Select specific ${fieldType}`}
                          onClick={() => setMode(Mode.ABSOLUTE)}
                        />
                        <NavItem
                          dataLoc={`${dataLoc}-select-relative`}
                          label={`Select relative ${fieldType}`}
                          onClick={() => setMode(Mode.RELATIVE)}
                        />
                      </>
                    )}
                  </PopoverPanel>
                )}
              </AnimatePresence>
            </>
          )}
        </Popover>
      </div>
    </Tooltip>
  );
};
